import type { ButtonHTMLAttributes, ReactElement, Ref } from 'react';

import styles from './Button.module.css';
import { DEFAULT_HUE, type Hue } from './hue';
import { Icon } from './Icon';
import type { IconName } from './iconName';

/**
 * The button — docs/DESIGN.md §6.1, ADR-0017, ADR-0101 §7.1.
 *
 * `label` is a required STRING rather than free children, and that is load-bearing twice
 * over. It is the visible text and the accessible name at the same time, so no width can
 * leave a control nameless; and it makes it impossible to smuggle markup into a control the
 * design specifies as one line of sans. Rich content belongs in a Banner or a Toast.
 *
 * ═══ THREE VARIANTS, AND A HUE ═══
 *
 *   `primary` — the citrus hero: the ONE gradient control on a screen (ADR-0101 §1 rule 2).
 *               Format in the toolbar, or its stand-in on the empty and blocked screens.
 *   `tinted`  — the default. An outline and a faint tint of its hue at rest, filled with the
 *               hue on hover: the legacy "outline → fill" grammar the owner asked to keep.
 *   `bare`    — no edge and no tint: the ×, a dialog's close, the drawer's close.
 *
 * `hue` picks the family a tinted button speaks in (`./hue.ts` — one meaning per hue) and is
 * ignored by the other two variants, which each have exactly one look. It reaches the DOM as
 * `data-hue`, an attribute, so CSS Modules' hashing cannot rename it out from under the
 * stylesheet.
 *
 * ═══ LABELS ARE NEVER SHED ═══
 *
 * ADR-0100 dropped labels for icons below 900px and 640px (`collapse`). ADR-0101 removes that:
 * the wand icon was read as a pencil, and an icon-only Clear asks the user to guess at a
 * destructive action. A label may instead be SHORTENED below 960px — `compactLabel`, e.g.
 * "Example" for "Load example" — and the accessible name stays the full `label` at every
 * width, so the words a screen reader hears never change with the window.
 *
 * `ui/**` does not translate. `label`, `compactLabel`, `shortcutHint` and `title` arrive
 * already localised from the shell (ADR-0003, ADR-0018).
 */

export type ButtonVariant = 'primary' | 'tinted' | 'bare';

/** `md` = `--ctl` (44px on phones, 40 from 960); `sm` = `--ctl-sm` (44, 32 from 960). */
export type ButtonSize = 'md' | 'sm';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  readonly label: string;
  /** Defaults to `'tinted'`. */
  readonly variant?: ButtonVariant;
  /** The family a `tinted` button speaks in. Defaults to `'peri'`, the neutral one. */
  readonly hue?: Hue;
  readonly size?: ButtonSize;
  readonly icon?: IconName;
  /**
   * The shortcut printed on the control at rest, e.g. `'⌘⏎'`. Already rendered for the
   * platform by the caller — `ui/**` does not know whether this machine says ⌘ or Ctrl.
   * Printed from 1120px; below that it lives on in `title`.
   */
  readonly shortcutHint?: string;
  /**
   * A shorter visible label for widths below 960px ("Example" for "Load example"). Visual
   * only: it is `aria-hidden`, and the accessible name remains `label` at every width.
   */
  readonly compactLabel?: string;
  /**
   * A count drawn as a lemon pill after the label — the number of problems Fix all would
   * repair. Rendered only when greater than zero, and `aria-hidden`: the pill repeats a number
   * the status line already announces, and an accessible name that changed with every
   * keystroke would re-announce the button on each one.
   */
  readonly badge?: number;
  readonly ref?: Ref<HTMLButtonElement>;
}

const VARIANT: Record<ButtonVariant, string> = {
  primary: styles.primary ?? '',
  tinted: '',
  bare: styles.bare ?? '',
};

export function Button({
  label,
  variant = 'tinted',
  hue = DEFAULT_HUE,
  size = 'md',
  icon,
  shortcutHint,
  compactLabel,
  badge,
  className,
  type = 'button',
  ...rest
}: ButtonProps): ReactElement {
  const hasCompact = compactLabel !== undefined && compactLabel !== label;
  const classes = [
    styles.base,
    VARIANT[variant],
    size === 'sm' ? styles.sm : undefined,
    hasCompact ? styles.hasCompact : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    // `type="button"` by default: a toolbar action that submits a form by accident is a bug
    // nobody finds until the day a form appears on the page.
    <button
      type={type}
      className={classes}
      aria-label={label}
      // Only a tinted button has a hue to name. The hero and the bare button each have one look,
      // and an attribute that said `peri` on the citrus hero would be a lie to anyone reading it.
      data-hue={variant === 'tinted' ? hue : undefined}
      {...rest}
    >
      {icon === undefined ? null : <Icon name={icon} />}
      <span className={styles.label}>{label}</span>
      {hasCompact ? (
        <span className={styles.compact} aria-hidden="true">
          {compactLabel}
        </span>
      ) : null}
      {badge !== undefined && badge > 0 ? (
        <span className={styles.badge} aria-hidden="true">
          {badge}
        </span>
      ) : null}
      {shortcutHint === undefined ? null : <kbd>{shortcutHint}</kbd>}
    </button>
  );
}

export default Button;
